import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart,
} from 'recharts';
import { CircularProgress } from '../CircularProgress';
import { KPICell, SectionFrame, performanceColor } from '../scheduled/shared';
import type { SalesOrdersDashboardData } from '../../../lib/api/sales-orders-dashboard';
import type { OpenDrillDown } from './SalesOrdersShell';

const PALETTE = {
  otif: '#0f766e',
  otd:  '#0284c7',
  spend: '#2563eb',
  open: '#cbd5e1',
  target: '#94a3b8',
};

const TARGET_PCT = 95;

interface Props {
  data: SalesOrdersDashboardData;
  formatCurrency: (n: number) => string;
  openDrillDown: OpenDrillDown;
  /** Feature-flag gate — when false, OTIF tile/trend line is not rendered. */
  showOtif: boolean;
  /** Supplier-portal install dispatches goods — relabel "Received" → "Delivered". */
  isSupplierPortalMode: boolean;
  /**
   * Customer (counterparty / customer-side) viewers see ONLY the top KPI cards —
   * the monthly trend charts below are hidden. Supplier/admin viewers keep all.
   */
  isCustomer: boolean;
}

/** YTD panel: same three-section KPIs + monthly trend charts. */
export function YTD({ data, formatCurrency, openDrillDown, showOtif, isSupplierPortalMode, isCustomer }: Props) {
  const ytd = data.ytd;
  const deliveredLabel = isSupplierPortalMode ? 'Delivered' : 'Received';
  const months = data.monthly?.months ?? [];

  const ytdPeriod = {
    start: ytd.window_start,
    end: ytd.window_end,
    label: 'YTD',
  };

  const trend = months.map((m) => ({
    month: m.label,
    month_start: m.month_start,
    month_end: m.month_end,
    otif: m.delivery.otif_pct,
    otd: m.delivery.otd_pct,
    received: m.cost.received_value,
    // Outstanding = LIVE outstanding value of lines due that month
    // (open_value + arrears_value). Excludes stale closed lines that were
    // received short (committed − delivered would wrongly count those).
    open: m.cost.open_value + m.cost.arrears_value,
    target: TARGET_PCT,
  }));

  // ---- Drill-down click handlers for charts ----
  const handleMonthPointClick = (
    monthStart: string | undefined,
    monthEnd: string | undefined,
    label: string | undefined,
  ) => {
    if (!monthStart || !monthEnd) return;
    openDrillDown({
      scope: 'delivered',
      period: { start: monthStart, end: monthEnd, label: label || 'month' },
      title: `${deliveredLabel} — ${label || 'selected month'}`,
      subtitle: 'Delivered — due that month',
    });
  };

  const handleMonthBarClick = (
    monthStart: string | undefined,
    monthEnd: string | undefined,
    label: string | undefined,
    seriesKey: 'received' | 'open',
  ) => {
    if (!monthStart || !monthEnd) return;
    if (seriesKey === 'received') {
      openDrillDown({
        scope: 'delivered',
        period: { start: monthStart, end: monthEnd, label: label || 'month' },
        title: `${deliveredLabel} Value — ${label || 'selected month'}`,
        subtitle: 'Lines due that month that were received',
      });
    } else {
      openDrillDown({
        scope: 'outstanding',
        period: { start: monthStart, end: monthEnd, label: label || 'month' },
        title: `Outstanding — ${label || 'selected month'}`,
        subtitle: 'Lines due that month not yet fully delivered',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Order Status" accent="red" subtitle="point-in-time snapshot">
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label="Open Lines (total)"
              value={<span className="text-2xl font-semibold tabular-nums">{(ytd.order_status.open_count ?? 0).toLocaleString()}</span>}
              foot="all open, incl. overdue"
              onClick={() => openDrillDown({
                scope: 'open_all',
                period: null,
                title: 'Open Lines (total)',
                subtitle: 'Whole open book — pending/partial, incl. overdue',
              })}
            />
            <KPICell
              label="On Hold"
              value={<span className="text-2xl font-semibold tabular-nums">{ytd.order_status.on_hold_count.toLocaleString()}</span>}
              foot="held PO lines"
              onClick={() => openDrillDown({
                scope: 'on_hold',
                period: null,
                title: 'On-Hold PO Lines',
                subtitle: 'Currently held',
              })}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle="Jan 1 → today">
          <div className={`grid ${showOtif ? 'grid-cols-2' : 'grid-cols-1'} gap-3 items-start`}>
            {showOtif && (
              <KPICell
                label="OTIF"
                value={<CircularProgress percentage={ytd.delivery.otif_pct} size={68} strokeWidth={6} color={performanceColor(ytd.delivery.otif_pct)} />}
                foot={`${ytd.delivery.otif_met.toLocaleString()}/${ytd.delivery.total_due.toLocaleString()} order lines`}
                onClick={() => openDrillDown({
                  scope: 'otif_miss',
                  period: ytdPeriod,
                  title: 'OTIF Misses — YTD',
                  subtitle: 'Past-due lines that missed on-time-in-full',
                })}
              />
            )}
            <KPICell
              label="OTD"
              value={<CircularProgress percentage={ytd.delivery.otd_pct} size={68} strokeWidth={6} color={performanceColor(ytd.delivery.otd_pct)} />}
              foot={`${ytd.delivery.otd_met.toLocaleString()}/${ytd.delivery.total_due.toLocaleString()} on time`}
              onClick={() => openDrillDown({
                scope: 'late',
                period: ytdPeriod,
                title: 'OTD Misses — YTD',
                subtitle: 'Past-due lines that missed on-time delivery',
              })}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="open book now · delivered YTD">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open Order Lines"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.open_orders_value)}</span>}
              foot={`${(data.portfolio.open_lines ?? 0).toLocaleString()} open lines (not yet late)`}
              onClick={() => openDrillDown({
                scope: 'open',
                period: null,
                title: 'Open Order Lines',
                subtitle: 'Live lines still open and not yet late',
              })}
            />
            <KPICell
              label={`${deliveredLabel} (YTD)`}
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(ytd.cost.received_value)}</span>}
              foot={`of ${formatCurrency(ytd.cost.total_value)} committed`}
              onClick={() => openDrillDown({
                scope: 'delivered',
                period: ytdPeriod,
                title: `${deliveredLabel} Value — YTD`,
                subtitle: 'Lines due this year that were received',
              })}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.arrears_value)}</span>}
              foot={`${(data.portfolio.arrears_lines ?? 0).toLocaleString()} overdue lines${data.portfolio.arrears_value === 0 && (data.portfolio.arrears_lines ?? 0) > 0 ? ' • unpriced in source' : ''}`}
              onClick={() => openDrillDown({
                scope: 'arrears',
                period: null,
                title: 'Arrears',
                subtitle: 'All overdue lines still open',
              })}
            />
          </div>
        </SectionFrame>
      </div>

      {/* Customer (counterparty) viewers see ONLY the top KPI cards above — the
          monthly trend charts below are supplier/admin-only. */}
      {!isCustomer && (months.length > 0 ? (
        <>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">{showOtif ? 'OTIF & OTD' : 'OTD'} — Monthly Trend</CardTitle>
              <p className="text-[11px] text-muted-foreground">Target {TARGET_PCT}% (dashed) • click a point to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => `${Math.round(v)}%`} />
                    <ReferenceLine y={TARGET_PCT} stroke={PALETTE.target} strokeDasharray="4 4" />
                    {showOtif && (
                      <Line type="monotone" dataKey="otif" stroke={PALETTE.otif} strokeWidth={2.5} dot={{ r: 3 }} name="OTIF %"
                        activeDot={{
                          r: 5,
                          style: { cursor: 'pointer' },
                          onClick: (_e: unknown, payload: unknown) => {
                            const p = payload as { payload?: { month_start?: string; month_end?: string; month?: string } };
                            handleMonthPointClick(p?.payload?.month_start, p?.payload?.month_end, p?.payload?.month);
                          },
                        }}
                      />
                    )}
                    <Line type="monotone" dataKey="otd"  stroke={PALETTE.otd}  strokeWidth={2.5} dot={{ r: 3 }} name="OTD %"
                      activeDot={{
                        r: 5,
                        style: { cursor: 'pointer' },
                        onClick: (_e: unknown, payload: unknown) => {
                          const p = payload as { payload?: { month_start?: string; month_end?: string; month?: string } };
                          handleMonthPointClick(p?.payload?.month_start, p?.payload?.month_end, p?.payload?.month);
                        },
                      }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">Monthly Order-Line Value — {deliveredLabel} vs Outstanding</CardTitle>
              <p className="text-[11px] text-muted-foreground">PO lines due that month, split by what's been delivered vs still outstanding</p>
            </CardHeader>
            <CardContent>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000000 ? `£${(v/1000000).toFixed(1)}M` : v >= 1000 ? `£${(v/1000).toFixed(0)}k` : `£${v}`} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                    <Bar dataKey="received" stackId="cost" fill={PALETTE.spend} radius={[0, 0, 0, 0]} name={deliveredLabel}
                      onClick={(d) => { const p = d?.payload as { month_start?: string; month_end?: string; month?: string } | undefined; handleMonthBarClick(p?.month_start, p?.month_end, p?.month, 'received'); }}
                      style={{ cursor: 'pointer' }} />
                    <Bar dataKey="open"     stackId="cost" fill={PALETTE.open}  radius={[3, 3, 0, 0]} name="Outstanding"
                      onClick={(d) => { const p = d?.payload as { month_start?: string; month_end?: string; month?: string } | undefined; handleMonthBarClick(p?.month_start, p?.month_end, p?.month, 'open'); }}
                      style={{ cursor: 'pointer' }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-slate-600 font-medium">Monthly trend</p>
            <p className="text-xs text-slate-500 mt-1">
              Monthly trend will appear after the next dashboard refresh.
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
